"use client";

import Link from "next/link";
import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../../../../i18n/client";
import NavigationButton from "./NavigationButton";
import { Routes } from "../../../(utils)/routes";
import { createContactUs } from "../../../../../utils/services/dashboard";
import "react-toastify/dist/ReactToastify.css";

interface ContactUsProps {
  lng: string;
  activateCheckbox?: (key: string) => void;
  checkedItems?: { [key: string]: boolean };
}

const ContactUs: React.FC<ContactUsProps> = ({
  lng,
  activateCheckbox: externalActivateCheckbox,
  checkedItems: externalCheckedItems,
}) => {
  const { t }: { t: any } = useTranslation(lng, "new-landing");
  const [internalCheckedItems, setInternalCheckedItems] = useState<{
    [key: string]: boolean;
  }>({
    "omto-dktb": false,
    "intelligent-modules": false,
  });

  const checkedItems = externalCheckedItems || internalCheckedItems;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleCheckboxChange = (key: string) => {
    if (externalCheckedItems && externalActivateCheckbox) {
      externalActivateCheckbox(key);
    } else {
      setInternalCheckedItems((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("form.errors.first-name-required");
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t("form.errors.last-name-required");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("form.errors.email-required");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("form.errors.email-invalid");
    }

    if (!formData.message.trim()) {
      newErrors.message = t("form.errors.message-required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { mutate, isPending } = useMutation({
    mutationFn: createContactUs,
    onSuccess: () => {
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        message: "",
      });
      setInternalCheckedItems({
        "omto-dktb": false,
        "intelligent-modules": false,
      });
      toast.success(t("form.success-message"));
    },
    onError: () => {
      toast.error(t("form.error-message"));
    },
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("demo_requested", "0");
    if (formData.firstName)
      formDataToSend.append("first_name", formData.firstName);
    if (formData.lastName)
      formDataToSend.append("last_name", formData.lastName);
    if (formData.email) formDataToSend.append("email", formData.email);
    if (formData.company)
      formDataToSend.append("company_name", formData.company);
    if (formData.phone) formDataToSend.append("phone_number", formData.phone);
    if (formData.message) formDataToSend.append("message", formData.message);
    formDataToSend.append(
      "digital_transport_order",
      checkedItems["omto-dktb"] ? "1" : "0",
    );
    formDataToSend.append(
      "intelligent_form_recognition",
      checkedItems["intelligent-modules"] ? "1" : "0",
    );

    mutate(formDataToSend);
  };

  return (
    <section
      id="contact"
      className="bg-white min-h-screen pt-[24px] pb-16 lg:pb-[100px]"
    >
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        className="custom-toast-container"
        toastClassName="custom-toast"
        bodyClassName="custom-toast-body"
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12 lg:mb-16">
          <div className="mb-12 sm:mb-16 flex justify-center">
            <NavigationButton
              text={t("contact-navigation")}
              targetId="products"
              direction="up"
              textClassName="text-primary-dark font-bold"
            />
          </div>

          <h2 className="text-[28px] sm:text-[48px] leading-[1.4] font-bold text-primary-dark mb-6">
            {t("contact-title")}
          </h2>
          <p className="text-[18px] text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {t("contact-description")}
          </p>
        </div>

        <div className="relative flex flex-col-reverse gap-4 lg:block">
          <div className="bg-white shadow-lg rounded-2xl w-full lg:max-w-[950px] p-6 sm:p-12 flex justify-end ml-auto">
            <form
              className="w-full lg:max-w-[530px] ml-auto"
              onSubmit={onSubmit}
            >
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor="firstName"
                      className="block text-base font-medium text-primary-dark mb-3"
                    >
                      {t("form.first-name")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder={t("form.placeholders.first-name")}
                      value={formData.firstName}
                      onChange={handleInputChange}
                      aria-describedby={
                        errors.firstName ? "firstName-error" : undefined
                      }
                      className={`form-input ${
                        errors.firstName
                          ? "form-input-error"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.firstName && (
                      <p
                        id="firstName-error"
                        className="form-error"
                        role="alert"
                      >
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="lastName"
                      className="block text-base font-medium text-primary-dark mb-3"
                    >
                      {t("form.last-name")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder={t("form.placeholders.last-name")}
                      value={formData.lastName}
                      onChange={handleInputChange}
                      aria-describedby={
                        errors.lastName ? "lastName-error" : undefined
                      }
                      className={`form-input ${
                        errors.lastName ? "form-input-error" : "border-gray-300"
                      }`}
                    />
                    {errors.lastName && (
                      <p
                        id="lastName-error"
                        className="form-error"
                        role="alert"
                      >
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-base font-medium text-primary-dark mb-3"
                  >
                    {t("form.company")}
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    placeholder={t("form.placeholders.company")}
                    value={formData.company}
                    onChange={handleInputChange}
                    aria-describedby={
                      errors.company ? "company-error" : undefined
                    }
                    className={`form-input ${
                      errors.company ? "form-input-error" : "border-gray-300"
                    }`}
                  />
                  {errors.company && (
                    <p id="company-error" className="form-error" role="alert">
                      {errors.company}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-base font-medium text-primary-dark mb-3"
                  >
                    {t("form.email")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t("form.placeholders.email")}
                    value={formData.email}
                    onChange={handleInputChange}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    className={`form-input ${
                      errors.email ? "form-input-error" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p id="email-error" className="form-error" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-base font-medium text-primary-dark mb-3"
                  >
                    {t("form.phone")}
                  </label>
                  <div className="relative">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder=""
                      value={formData.phone}
                      onChange={handleInputChange}
                      aria-describedby={
                        errors.phone ? "phone-error" : undefined
                      }
                      className={`form-input pl-16 ${
                        errors.phone ? "form-input-error" : "border-gray-300"
                      }`}
                    />
                    <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-black font-medium">
                      +49
                    </div>
                    <div className="absolute left-14 top-3 bottom-3 w-px bg-gray-300" />
                  </div>
                  {errors.phone && (
                    <p id="phone-error" className="form-error" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-base font-medium text-primary-dark mb-3"
                  >
                    {t("form.message")} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    placeholder={t("form.placeholders.message")}
                    value={formData.message}
                    onChange={handleInputChange}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                    className={`form-input resize-none ${
                      errors.message ? "form-input-error" : "border-gray-300"
                    }`}
                  />
                  {errors.message && (
                    <p id="message-error" className="form-error" role="alert">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-base font-medium text-primary-dark mb-4">
                    {t("form.select-product")}
                  </p>
                  <div className="space-y-4">
                    <button
                      type="button"
                      className="flex items-center gap-2 focus:outline-none"
                      onClick={() => handleCheckboxChange("omto-dktb")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleCheckboxChange("omto-dktb");
                        }
                      }}
                      aria-pressed={checkedItems["omto-dktb"]}
                      aria-label="Toggle Digital Patient Transport Ordering"
                    >
                      <input
                        type="checkbox"
                        name="omto-dktb"
                        className="sr-only"
                        checked={checkedItems["omto-dktb"]}
                        onChange={() => handleCheckboxChange("omto-dktb")}
                      />
                      <div
                        className={`form-checkbox focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded ${
                          checkedItems["omto-dktb"]
                            ? "form-checkbox-checked"
                            : "form-checkbox-unchecked"
                        }`}
                      >
                        <svg
                          className={`w-3 h-3 ${
                            checkedItems["omto-dktb"]
                              ? "text-white"
                              : "text-blue-500 opacity-0"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-primary-dark leading-relaxed text-left">
                        {t("form.product-omto-dktb")}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="flex items-center gap-2 focus:outline-none"
                      onClick={() =>
                        handleCheckboxChange("intelligent-modules")
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleCheckboxChange("intelligent-modules");
                        }
                      }}
                      aria-pressed={checkedItems["intelligent-modules"]}
                      aria-label="Toggle Intelligent software modules"
                    >
                      <input
                        type="checkbox"
                        name="intelligent-modules"
                        className="sr-only"
                        checked={checkedItems["intelligent-modules"]}
                        onChange={() =>
                          handleCheckboxChange("intelligent-modules")
                        }
                      />
                      <div
                        className={`form-checkbox focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded ${
                          checkedItems["intelligent-modules"]
                            ? "form-checkbox-checked"
                            : "form-checkbox-unchecked"
                        }`}
                      >
                        <svg
                          className={`w-3 h-3 ${
                            checkedItems["intelligent-modules"]
                              ? "text-white"
                              : "text-blue-500 opacity-0"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-primary-dark leading-relaxed text-left">
                        {t("form.product-intelligent-modules")}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-primary-dark mb-8 text-justify">
                    {t("form.privacy-agreement-start")}{" "}
                    <Link
                      href={`${Routes.PrivacyPolicyNew}`}
                      className="underline text-[#3B82F6] hover:opacity-70 transition-opacity duration-300"
                    >
                      {t("form.privacy-policy")}
                    </Link>{" "}
                    {t("form.privacy-agreement-middle")}{" "}
                    <Link
                      href={`${Routes.PrivacyPolicyNew}#cookies`}
                      className="underline text-[#3B82F6] hover:opacity-70 transition-opacity duration-300"
                    >
                      {t("form.cookie-policy")}
                    </Link>
                    {t("form.privacy-agreement-end")}
                  </p>

                  <button
                    type="submit"
                    disabled={isPending}
                    className={`w-full btn-base btn-lg btn-focus rounded-lg font-extrabold ${
                      isPending
                        ? "btn-disabled bg-gray-400"
                        : "bg-primary-blue hover:bg-blue-700"
                    } text-white`}
                  >
                    {isPending
                      ? t("form.submitting-button")
                      : t("form.submit-button")}
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="lg:absolute lg:top-1/2 lg:left-0 lg:-translate-y-1/2 w-full lg:max-w-[400px] xl:max-w-[490px]">
            <div className="bg-gray-100 rounded-xl p-6 sm:p-8 shadow-lg">
              <h3 className="text-[26px] sm:text-[28px] leading-[1.4] font-semibold text-primary-dark mb-12">
                {t("contact-info.title")}
              </h3>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 flex-shrink-0">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="w-full h-full"
                    >
                      <path
                        d="M21 16.42v3.536a1 1 0 01-.93.998c-.437.03-.794.046-1.07.046-8.837 0-16-7.163-16-16 0-.276.015-.633.046-1.07A1 1 0 014.044 3H7.58a.5.5 0 01.498.45c.023.23.044.413.064.552A13.901 13.901 0 009.35 8.003c.095.2.033.439-.147.567l-2.158 1.542a13.047 13.047 0 006.844 6.844l1.54-2.154a.462.462 0 01.573-.149 13.901 13.901 0 004.001 1.205c.139.02.322.042.55.064a.5.5 0 01.451.498z"
                        className="fill-primary-dark"
                      />
                    </svg>
                  </div>
                  <a
                    href={`tel:${t("contact-info.phone")}`}
                    className="text-[16px] sm:text-[20px] font-medium text-primary-dark hover:opacity-70 transition-opacity duration-200"
                  >
                    {t("contact-info.phone")}
                  </a>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 flex-shrink-0">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="w-full h-full"
                    >
                      <path
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        className="stroke-primary-dark"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </div>
                  <a
                    href={`mailto:${t("contact-info.email")}`}
                    className="text-[16px] sm:text-[20px] font-medium text-primary-dark hover:opacity-70 transition-opacity duration-200"
                  >
                    {t("contact-info.email")}
                  </a>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex-shrink-0 mt-1">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="w-full h-full"
                    >
                      <path
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                        className="fill-primary-dark"
                      />
                    </svg>
                  </div>
                  <div className="text-[16px] sm:text-[20px] font-medium text-primary-dark leading-relaxed">
                    {t("contact-info.address")}
                    <br />
                    {t("contact-info.address-city")}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-12 lg:mt-52">
                <Link
                  href="https://instagram.com/omtomedical"
                  aria-label="Visit OMTO Instagram page"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity duration-200">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 0C14.3261 0 15.5979 0.526784 16.5355 1.46447C17.4732 2.40215 18 3.67392 18 5V13C18 14.3261 17.4732 15.5979 16.5355 16.5355C15.5979 17.4732 14.3261 18 13 18H5C3.67392 18 2.40215 17.4732 1.46447 16.5355C0.526784 15.5979 0 14.3261 0 13V5C0 3.67392 0.526784 2.40215 1.46447 1.46447C2.40215 0.526784 3.67392 0 5 0H13ZM9 5C7.93913 5 6.92172 5.42143 6.17157 6.17157C5.42143 6.92172 5 7.93913 5 9C5 10.0609 5.42143 11.0783 6.17157 11.8284C6.92172 12.5786 7.93913 13 9 13C10.0609 13 11.0783 12.5786 11.8284 11.8284C12.5786 11.0783 13 10.0609 13 9C13 7.93913 12.5786 6.92172 11.8284 6.17157C11.0783 5.42143 10.0609 5 9 5ZM9 7C9.53043 7 10.0391 7.21071 10.4142 7.58579C10.7893 7.96086 11 8.46957 11 9C11 9.53043 10.7893 10.0391 10.4142 10.4142C10.0391 10.7893 9.53043 11 9 11C8.46957 11 7.96086 10.7893 7.58579 10.4142C7.21071 10.0391 7 9.53043 7 9C7 8.46957 7.21071 7.96086 7.58579 7.58579C7.96086 7.21071 8.46957 7 9 7ZM13.5 3.5C13.2348 3.5 12.9804 3.60536 12.7929 3.79289C12.6054 3.98043 12.5 4.23478 12.5 4.5C12.5 4.76522 12.6054 5.01957 12.7929 5.20711C12.9804 5.39464 13.2348 5.5 13.5 5.5C13.7652 5.5 14.0196 5.39464 14.2071 5.20711C14.3946 5.01957 14.5 4.76522 14.5 4.5C14.5 4.23478 14.3946 3.98043 14.2071 3.79289C14.0196 3.60536 13.7652 3.5 13.5 3.5Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </Link>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.linkedin.com/company/omtomedical/"
                  aria-label="Visit OMTO LinkedIn page"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity duration-200">
                    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
                      <path
                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
